// @ts-strict-ignore
import React, { memo, useEffect } from 'react'
import { Helmet } from 'react-helmet'
import Condition from 'components/Condition'
import Page from 'components/Page'
import PageHeader from 'components/PageHeader'
import PageHeaderTitle from 'components/PageHeaderTitle'
import { graphql } from 'relay-runtime'
import { PreloadedQuery, usePreloadedQuery } from 'react-relay'
import { BlogPost_Query } from '__generated__/BlogPost_Query.graphql'
import { useFragment } from 'react-relay/hooks'
import { BlogPost_BlogPostFragment$key } from '__generated__/BlogPost_BlogPostFragment.graphql'
import { useIsGuest } from 'hooks/user'
import { useQuickCommit } from 'hooks/useQuickCommit'
import { BlogPost_User$key } from '__generated__/BlogPost_User.graphql'
import storageGated from 'services/localStorage'
import { BlogPost_TrackViewMutation } from '__generated__/BlogPost_TrackViewMutation.graphql'
import BlogPostLanding from '../BlogPostLanding/BlogPostLanding'
import BlogPostSignedIn from '../BlogPostSignedIn/BlogPostSignedIn'

export const BlogPostQuery = graphql`
  query BlogPost_Query($slug: String!) {
    currentUser {
      ...BlogPost_User
      ...auth_checkAuthForPage
    }
    blogPost(slug: $slug) {
      ...BlogPost_BlogPostFragment
    }
  }
`

function BlogPost({ preloadedQuery }: { preloadedQuery: PreloadedQuery<BlogPost_Query> }) {
  const data = usePreloadedQuery<BlogPost_Query>(BlogPostQuery, preloadedQuery)

  const blogPost = useFragment<BlogPost_BlogPostFragment$key>(graphql`
    fragment BlogPost_BlogPostFragment on BlogPost {
      id
      title
      status
      excerpt
      content
      user {
        name
        avatarUrl
      }
      updatedAt
      ...BlogPostLanding_BlogPost
      ...BlogPostSignedIn_BlogPost
    }
  `, data?.blogPost)

  const user = useFragment<BlogPost_User$key>(graphql`
    fragment BlogPost_User on User {
      ...user_useIsGuest
    }
  `, data?.currentUser)

  const guest = useIsGuest(user)

  const { execute: executeIncrementView } = useQuickCommit<BlogPost_TrackViewMutation>(graphql`
    mutation BlogPost_TrackViewMutation($input: TrackBlogPostViewInput!) {
      trackBlogPostView(input: $input) {
        blogPost {
          id
        }
      }
    }
  `)

  const incrementViewCount = async (blogPostId: string) => {
    const viewedKey = `viewed_post_${blogPostId}`
    const hasViewed = storageGated.getItem(viewedKey)

    if (!hasViewed) {
      try {
        storageGated.setItem(viewedKey, 'true')
        await executeIncrementView({ input: { blogPostId } })
      } catch (e) {
        console.log(e)
      }
    }
  }

  useEffect(() => {
    if (blogPost && blogPost && blogPost.status === 'approved') {
      incrementViewCount(blogPost.id)
    }
  }, [blogPost])

  if (!blogPost) {
    return (
      <Page>
        <PageHeader>
          <PageHeaderTitle>
            Not Found
          </PageHeaderTitle>
        </PageHeader>
      </Page>
    )
  }

  return (
    <React.Fragment>
      <Helmet>
        <title>{blogPost?.title}</title>
        <meta name="description" content={(blogPost?.content || '').substring(0, 150)} />
        <meta property="og:title" content={blogPost?.title} />
        <meta property="og:description" content={(blogPost?.content || '').substring(0, 150)} />
        <script type="application/ld+json">
          {JSON.stringify({
            '@context': 'http://schema.org',
            '@type': 'BlogPosting',
            headline: (blogPost?.title || '').substring(0, 110),
            description: blogPost?.excerpt,
            articleBody: blogPost?.content,
            author: {
              name: blogPost?.user?.name,
              ...(blogPost?.user?.avatarUrl && { image: blogPost?.user?.avatarUrl }),
            },
            datePublished: blogPost?.updatedAt,
            publisher: {
              '@context': 'https://schema.org',
              '@type': 'Organization',
              name: 'Flexhire',
              url: 'https://flexhire.com',
              logo: 'https://flexhire-images.s3.amazonaws.com/flexhire-logo-white.png',
              slogan: 'Connecting Talent & Opportunity',
              address: {
                '@type': 'PostalAddress',
                streetAddress: '7809 Prospector Drive, Cottonwood Heights',
                addressLocality: 'Salt Lake City',
                addressRegion: 'UT',
                postalCode: '84121',
                country: 'USA',
              },
              email: 'info@flexhire.com',
            },
          })}
        </script>
      </Helmet>

      <Condition condition={guest}>
        <BlogPostLanding blogPostFragmentRef={blogPost} />
      </Condition>

      <Condition condition={!guest}>
        <BlogPostSignedIn blogPostFragmentRef={blogPost} />
      </Condition>
    </React.Fragment>
  )
}

export default memo(BlogPost)
